/**
 * colorScales.ts
 *
 * Shared color gradients for temperature, mean-sea-level pressure (MSLP) and
 * terrain hypsometric tinting. Each export also provides a CSS gradient
 * string so the on-screen legends can reuse the exact same stops as the 3D
 * materials.
 */

import * as THREE from "three";

interface Stop {
  t: number;
  hex: number;
  /** Precomputed once per stop (not per sample) -- constructing a THREE.Color does an
   *  expensive sRGB-to-linear conversion, and sampleStops() below is called tens of
   *  thousands of times per frame (once per particle, for both the head marker and every
   *  trail segment), so allocating fresh THREE.Color objects per call was a major CPU cost. */
  color: THREE.Color;
}

function makeStops(raw: { t: number; hex: number }[]): Stop[] {
  return raw.map((s) => ({ t: s.t, hex: s.hex, color: new THREE.Color(s.hex) }));
}

function sampleStops(stops: Stop[], t: number, target: THREE.Color): THREE.Color {
  const clamped = Math.min(1, Math.max(0, t));
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (clamped >= a.t && clamped <= b.t) {
      const localT = (clamped - a.t) / (b.t - a.t);
      target.r = a.color.r + (b.color.r - a.color.r) * localT;
      target.g = a.color.g + (b.color.g - a.color.g) * localT;
      target.b = a.color.b + (b.color.b - a.color.b) * localT;
      return target;
    }
  }
  return target.copy(stops[stops.length - 1].color);
}

function toCssGradient(stops: Stop[]): string {
  const parts = stops.map((s) => `#${s.hex.toString(16).padStart(6, "0")} ${(s.t * 100).toFixed(0)}%`);
  return `linear-gradient(to right, ${parts.join(", ")})`;
}

// --- Temperature -----------------------------------------------------------
// This is earth.nullschool.net's actual temperature overlay color scale, copied directly from
// its open-source client (public/libs/earth/1.0.0/products.js, the "temp" product's
// segmentedColorScale) and used here for BOTH the globe's surface-temperature shell and
// particle coloring, so both match nullschool's look exactly rather than a custom scale.
const TEMPERATURE_STOPS: Stop[] = makeStops([
  // Nullschool's own t=0 stop (0x25042a) is near-black, which reads fine on their light/gray
  // map background but makes the coldest particles nearly invisible against our black space
  // background and hard to tell apart from "faded due to altitude". Brightened (same hue,
  // raised lightness floor) so extreme-cold particles stay visible; every other stop is
  // untouched and still matches nullschool exactly.
  { t: 0, hex: 0x5a0a66 }, // 193 K
  { t: 0.0963, hex: 0x290a82 }, // 206 K
  { t: 0.1926, hex: 0x512828 }, // 219 K
  { t: 0.2974, hex: 0xc02595 }, // 233.15 K (-40 C/F)
  { t: 0.462, hex: 0x46d7d7 }, // 255.372 K (0 F)
  { t: 0.5937, hex: 0x1554bb }, // 273.15 K (0 C)
  { t: 0.6085, hex: 0x18840e }, // 275.15 K (just above 0 C)
  { t: 0.7259, hex: 0xf7fb3b }, // 291 K
  { t: 0.7778, hex: 0xeba715 }, // 298 K
  { t: 0.8741, hex: 0xe64727 }, // 311 K
  { t: 1, hex: 0x581b43 } // 328 K
]);
export const TEMPERATURE_MIN_K = 193;
export const TEMPERATURE_MAX_K = 328;
export const TEMPERATURE_GRADIENT_CSS = toCssGradient(TEMPERATURE_STOPS);

export function temperatureToColor(tempK: number, target = new THREE.Color()): THREE.Color {
  const t = (tempK - TEMPERATURE_MIN_K) / (TEMPERATURE_MAX_K - TEMPERATURE_MIN_K);
  return sampleStops(TEMPERATURE_STOPS, t, target);
}

// --- Mean sea-level pressure -------------------------------------------------
// Purple (extreme low) -> blue (low) -> crossover around 1010 hPa (roughly
// standard sea-level pressure) -> yellow (high) -> gold (extreme high). The
// low->high crossover is a fairly narrow band (t=0.44-0.56, ~1006-1014 hPa)
// -- eased slightly wider than an earlier very-sharp version per feedback,
// but still much narrower than a wide blend through a gray/white-ish
// midpoint, so the whole gradient still reads as more saturated overall.

const MSLP_STOPS: Stop[] = makeStops([
  { t: 0, hex: 0x5b2a86 }, // 980 hPa -- purple (extreme low)
  { t: 0.3, hex: 0x1b4f91 }, // 998 hPa -- blue (low)
  { t: 0.44, hex: 0x2f6fc0 }, // 1006.4 hPa -- blue, right before the crossover
  { t: 0.56, hex: 0xd9b24a }, // 1013.6 hPa -- yellow, right after the crossover
  { t: 0.7, hex: 0xc9a227 }, // 1022 hPa -- yellow (high)
  { t: 1, hex: 0xf0c550 } // 1040 hPa -- gold (extreme high)
]);
export const MSLP_MIN_HPA = 980;
export const MSLP_MAX_HPA = 1040;
export const MSLP_GRADIENT_CSS = toCssGradient(MSLP_STOPS);

export function mslpToColor(hpa: number, target = new THREE.Color()): THREE.Color {
  const t = (hpa - MSLP_MIN_HPA) / (MSLP_MAX_HPA - MSLP_MIN_HPA);
  return sampleStops(MSLP_STOPS, t, target);
}

// --- Hypsometric terrain tint ------------------------------------------------
// Land color begins immediately AT sea level (t=0.5): a prior version kept a
// "shallow water" blue for ~240m above sea level, which made most real-world
// low-lying land (river deltas, coastal plains, flat basins) look flooded.

const HYPSO_STOPS: Stop[] = makeStops([
  { t: 0, hex: 0x081c3a },
  { t: 0.45, hex: 0x1f5fa8 },
  { t: 0.4999, hex: 0x3a7bd1 },
  { t: 0.5, hex: 0x3d7a4d },
  { t: 0.65, hex: 0xb59b4e },
  { t: 0.85, hex: 0x8a6d4b },
  { t: 1, hex: 0xffffff }
]);
export const HYPSO_MIN_KM = -6;
export const HYPSO_MAX_KM = 6;

export function hypsometricColor(elevationKm: number, target = new THREE.Color()): THREE.Color {
  const t = (elevationKm - HYPSO_MIN_KM) / (HYPSO_MAX_KM - HYPSO_MIN_KM);
  return sampleStops(HYPSO_STOPS, t, target);
}

